import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import {
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { GeoJSON } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { MapCanvas } from '@/components/maps/MapCanvas';
import { getParcel, searchParcels } from '../../api/parcels';

const ParcelPicker = ({ parcelId, onLink, onUnlink }) => {
  const [parcel, setParcel] = useState(null);
  const [searchRef, setSearchRef] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingParcel, setIsLoadingParcel] = useState(false);
  const [error, setError] = useState(null);

  // Fetch linked parcel data
  useEffect(() => {
    if (parcelId) {
      setIsLoadingParcel(true);
      getParcel(parcelId)
        .then((data) => {
          setParcel(data);
        })
        .catch((err) => {
          console.error('Error fetching parcel:', err);
          setError('Failed to load parcel data.');
        })
        .finally(() => {
          setIsLoadingParcel(false);
        });
    } else {
      setParcel(null);
    }
  }, [parcelId]);

  // Search for parcels by reference
  const handleSearch = async () => {
    if (!searchRef.trim()) return;

    setIsSearching(true);
    setError(null);
    try {
      const data = await searchParcels(searchRef.trim());
      setSearchResults(data.parcels || data || []);
    } catch (err) {
      console.error('Error searching parcels:', err);
      setError('Failed to search parcels.');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  if (isLoadingParcel) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // If a parcel is already linked, show its info
  if (parcelId && parcel) {
    const center = parcel.geometry
      ? (() => {
          // Try to get center from geometry bounding box
          try {
            const coords = parcel.geometry.coordinates[0];
            const lats = coords.map((c) => c[1]);
            const lngs = coords.map((c) => c[0]);
            return [
              (Math.min(...lats) + Math.max(...lats)) / 2,
              (Math.min(...lngs) + Math.max(...lngs)) / 2,
            ];
          } catch {
            return [-25.6429, 28.3434];
          }
        })()
      : [-25.6429, 28.3434];

    return (
      <Box>
        <Typography variant="body2" gutterBottom>
          Linked Parcel: <strong>{parcel.ref_number || parcel.id}</strong>
        </Typography>
        {parcel.geometry && (
          <Box sx={{ height: 200, mb: 2 }}>
            {/* Read-only parcel preview — no gear, no maximize, scroll
                wheel off so the form scroll isn't hijacked. */}
            <MapCanvas
              center={center}
              zoom={14}
              variant="inline"
              gearMode="none"
              maximize={false}
              scrollWheelZoom={false}
              className="!h-full !min-h-0"
            >
              <GeoJSON data={parcel.geometry} />
            </MapCanvas>
          </Box>
        )}
        {parcel.area_hectares && (
          <Typography variant="body2" color="text.secondary">
            Area: {parcel.area_hectares} hectares
          </Typography>
        )}
        <Button
          variant="outlined"
          color="error"
          size="small"
          startIcon={<LinkOffIcon />}
          onClick={onUnlink}
          sx={{ mt: 1 }}
        >
          Unlink Parcel
        </Button>
      </Box>
    );
  }

  // No parcel linked - show search
  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          size="small"
          label="Search parcel by reference"
          value={searchRef}
          onChange={(e) => setSearchRef(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          fullWidth
        />
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={isSearching}
          startIcon={isSearching ? <CircularProgress size={16} /> : <SearchIcon />}
        >
          Search
        </Button>
      </Box>
      {searchResults.length > 0 && (
        <Box>
          {searchResults.map((result) => (
            <Paper key={result.id} sx={{ p: { xs: 1, sm: 2 }, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="body2">
                  <strong>{result.ref_number || result.id}</strong>
                </Typography>
                {result.area_hectares && (
                  <Typography variant="caption" color="text.secondary">
                    {result.area_hectares} hectares
                  </Typography>
                )}
              </Box>
              <Button
                variant="contained"
                size="small"
                startIcon={<LinkIcon />}
                onClick={() => onLink(result.id)}
              >
                Link
              </Button>
            </Paper>
          ))}
        </Box>
      )}
      {searchResults.length === 0 && searchRef && !isSearching && (
        <Typography variant="body2" color="text.secondary">
          No parcels found. Try a different reference.
        </Typography>
      )}
    </Box>
  );
};

export default ParcelPicker;
